
import { defineComponent } from 'vue'
import { mapGetters, mapState } from 'vuex'
import { setI18nLanguage } from '@/i18n'
import { LanguagesType } from '@/types'
import { constants } from '@/constants'
import AsahiBsHeader from '@/components/library/AsahiBsHeader.vue'
import AsahiBsFooter from '@/components/library/AsahiBsFooter.vue'
import AsahiBsNav from '@/components/library/AsahiBsNav.vue'
import BreadcrumbLayout from '@/components/library/BreadcrumbLayout.vue'
import { RolePermissionDetails } from '@/store/modules/users/interfaces/IUser'
import { getValueByLabel } from '@/utils/permissions'
import { IPortalHardCodeLink } from '@/store/modules/links/interfaces/IPortalHardCodeLink'
import { LinksActionType } from '@/store/modules/links/interfaces'

interface RoleDetail {
  rolename: string;
  roleid: string;
  permission: string;
  roleType: string;
  customerType: string;
  possibleRoles: string;
}

export default defineComponent({
  name: 'HometLayout',
  components: {
    AsahiBsHeader,
    AsahiBsFooter,
    AsahiBsNav,
    BreadcrumbLayout
  },
  data: () => ({
    activeLanguage: (localStorage.getItem('lang')) as LanguagesType,
    theme: constants.application.theme

  }),

  computed: {
    ...mapState('basket', ['id', 'items', 'progressBarMsg']),
    ...mapState('catalog', ['wishlist']),
    ...mapGetters('main', ['getPermissions']),
    ...mapGetters('basket', ['progressBarMessage', 'progressBarSuccess', 'showProgressBarMessage']),
    isCartPage () {
      return this.$route.name === 'cart'
    },
    isDocumentPage () {
      return this.$route.name === 'myDocuments'
    },

    isBasicCustomer () {
      const customerTypeArray = this.$store.state.auth.user?.permissionSchema?.filter(permission => permission?.rpDetails.some((item: RoleDetail) => item?.customerType?.toLowerCase() !== ''))
      const customerType = customerTypeArray && customerTypeArray[0] && customerTypeArray[0]?.rpDetails[0] ? customerTypeArray[0]?.rpDetails[0]?.rolename : ''
      return (customerType.toLowerCase() === 'ontrade basic') || (customerType.toLowerCase() === 'distributor basic')
    },

    rightMenu () {
      const contract = this.isBasicCustomer
        ? []
        : this.checkPermission('Contracts', {
          label: this.$t('Contracts'),
          href: '/contracts',
          name: 'contracts',
          isActive: false,
          icon: 'file-contract'
        })

      const documents = this.isBasicCustomer
        ? []
        : this.checkPermission('My Documents', {
          label: this.$t('My Documents'),
          href: '/my-documents',
          name: 'myDocuments',
          isActive: false,
          icon: 'newspaper'
        })

      return [
        ...this.checkPermission('My Profile', {
          label: this.$t('My Profile'),
          href: '/my-profile/details',
          name: 'profile',
          isActive: false

        }),
        ...documents,
        ...this.checkPermission('Reporting', {
          label: this.$t('Reporting'),
          href: '/reporting',
          name: 'reporting',
          isActive: false,
          icon: 'newspaper'
        }),
        ...contract
      ]
    },
    customerType (): RolePermissionDetails | undefined {
      if (this.user && this.user?.permissionSchema?.length > 0) {
        return this.user?.permissionSchema[0]?.rpDetails?.find((item: RolePermissionDetails) => item.customerType)
      }
      return undefined
    },
    user () {
      return this.$store.state.auth.user
    },
    allPermission () {
      return this.user.allAvailablePermission
    },
    portalHardCodeLink (): IPortalHardCodeLink[] {
      return this.$store.state.links.portalHardCodeLink?.results ?? []
    },
    favoritesItems () {
      return this.wishlist?.items_v2?.shipto_item_count
    },
    languages () {
      return ['pl', 'en']
    },
    linksStore () {
      return this.$store.state.links
    },
    portalPageMenuLink () {
      return this.$store.state.links.portalPageMenuLink?.items ?? []
    },
    cartItems () {
      return this.items?.length
    },
    catalogPermission () {
      return this.checkPermission('Catalogue', {}).length !== 0
    },
    footerLinksBrands () {
      const beforeLoginBrands = [
        'brandTyskie', 'brandLech', 'brandZubr', 'brandKsiazece', 'brandVelkopopovicky', 'brandDebowe',
        'brandHardmade', 'brandPilsner', 'brandLechPils', 'brandPilsnerUrquell'
      ]
      let brandLinks

      if (this.user.country === 'SVK') {
        brandLinks = this.portalHardCodeLink
          .filter((data) => data.type.toLowerCase() === 'Mobile Brands PL'.toLowerCase() && beforeLoginBrands.includes(data.name))
          .map((data) => ({
            label: data.label,
            link: data.url,
            target: '_blank',
            order: data.order
          }))
          .sort((a, b) => a.order - b.order)
      } else {
        brandLinks = this.portalHardCodeLink
          .filter((data) => data.type.toLowerCase() === 'Mobile Brands PL'.toLowerCase() && beforeLoginBrands.includes(data.name))
          .map((data) => ({
            label: data.label,
            link: data.url,
            target: '_blank',
            order: data.order
          }))
          .sort((a, b) => a.order - b.order)
      }
      return [

        ...brandLinks
      ]
    },
    footerLinksCookies () {
      const aboutUsLinks = this.portalHardCodeLink
        .filter((data) => data.name === 'cookies')
        .map((data) => ({
          label: this.$t('Cookies'),
          href: data.url,
          target: '_blank'
        }))
      return aboutUsLinks
    },
    footerLinksTermsOfUse () {
      const aboutUsLinks = this.portalHardCodeLink
        .filter((data) => data.name === 'termsOfUse')
        .map((data) => ({
          label: this.$t('Terms of use'),
          href: data.url,
          target: '_blank'
        }))
      return aboutUsLinks
    },
    footerLinksPrivacyPolicy () {
      const aboutUsLinks = this.portalHardCodeLink
        .filter((data) => data.name === 'privacyPolicy')
        .map((data) => ({
          label: this.$t('Privacy policy'),
          href: data.url,
          target: '_blank'
        }))
      return aboutUsLinks
    }
  },

  methods: {
    changeLanguage (langCode: LanguagesType) {
      this.activeLanguage = langCode
      setI18nLanguage(langCode)
    },
    checkPermission (permission: string, value: Record<string, any>, additionalPremmision = true) {
      if (this.customerType) localStorage.setItem('customertype', this.customerType?.customerType)

      return this.allPermission?.includes(permission) &&
      additionalPremmision &&
      getValueByLabel(this.getPermissions(), permission)
        ? [value]
        : []
    },
    getLinkByName (name: string) {
      const linkObj = this.portalHardCodeLink.find((link) => link.name === name)
      return linkObj ? { url: linkObj.url, label: linkObj.label } : { url: '', label: '' }
    },
    async removeProductFromCart (productId: string) {
      if (this.$store.state.basket?.id && productId) {
        await this.$store.dispatch('basket/REMOVE_ITEM', {
          cart_id: this.$store.state.basket.id,
          cart_item_id: productId
        }, { root: true })
        await this.$store.dispatch('basket/FETCH_MIN_CART_QUERY_FOR_CATALOG', { cart_id: this.$store.state.basket.id }, { root: true })
      }
    },

    fetchPortalPageMenuLink () {
      const customerType = localStorage.getItem('customertype')
      this.$store.dispatch(LinksActionType.FETCH_PAGE_MENU_LINK, {
        CustomerType: customerType
      })
    }
  },
  watch: {
    activeLanguage (newLang, oldLang) {
      if (newLang !== oldLang) {
        this.fetchPortalPageMenuLink()
      }
    }
  }
})
